import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {Box, IconButton} from '@mui/material';
import {DateField} from '@mui/x-date-pickers-pro';
import {DateTime} from 'luxon';
import {GeoJSONFeature} from 'ol/format/GeoJSON';
import {type FC, useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {showModal} from '@src/actions/ui';
import {MODALS} from '@src/constants/modals';
import {useAvailableDates} from '@src/hooks/useAvailableDates';
import useDateTimeParams from '@src/hooks/useDateTimeParams';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import {isSingleDayDateRange} from '@src/utils/dates';

export type ReduxState = {
  map: {
    feature: {
      geometry: GeoJSONFeature;
    };
  };
};

const DatePicker: FC = () => {
  const {
    dateTimeFrom,
    dateTimeTo,
    parsedDateTimeFrom,
    parsedDateTimeTo,
    setDateTimeParams,
  } = useDateTimeParams();
  const {layerId} = useVisualizationParams();
  const dispatch = useDispatch();

  const mapFeature = useSelector((state: ReduxState) => state.map.feature);

  const {availableDates, setSearchRange} = useAvailableDates({
    from: parsedDateTimeTo?.startOf('month') ?? null,
    to: parsedDateTimeTo?.endOf('month') ?? null,
  });

  const isSingleDate = isSingleDayDateRange(
    parsedDateTimeFrom,
    parsedDateTimeTo,
  );

  const minDate = DateTime.fromISO('2000-01-01').startOf('day');
  const maxDate = DateTime.now().endOf('day');

  const handleSingleDayDateChange = useCallback(
    (newDateTime: DateTime) => {
      setDateTimeParams({
        dateTimeFrom: newDateTime.startOf('day').toISO(),
        dateTimeTo: newDateTime.endOf('day').toISO(),
      });
    },
    [setDateTimeParams],
  );

  const handleFromDateChange = (newDateTimeFrom: DateTime) => {
    setDateTimeParams({
      dateTimeFrom: newDateTimeFrom.startOf('day').toISO(),
      dateTimeTo: dateTimeTo,
    });
  };

  const handleToDateChange = (newDateTimeTo: DateTime) => {
    setDateTimeParams({
      dateTimeFrom: dateTimeFrom,
      dateTimeTo: newDateTimeTo.endOf('day').toISO(),
    });
  };

  const handlePrev = () => {
    if (!parsedDateTimeFrom) {
      return;
    }

    const previousDates = availableDates
      .filter(date => date < parsedDateTimeFrom)
      .sort((a, b) => b.toMillis() - a.toMillis());

    if (previousDates.length > 0) {
      handleSingleDayDateChange(previousDates[0]);
    } else {
      const prevMonth = parsedDateTimeFrom.minus({months: 1}).endOf('month');
      if (prevMonth < minDate) {
        return;
      }
      setSearchRange({
        from: prevMonth.startOf('month'),
        to: prevMonth.endOf('month'),
      });
    }
  };

  const handleNext = () => {
    if (!parsedDateTimeTo) {
      return;
    }

    const nextDates = availableDates
      .filter(date => date > parsedDateTimeTo)
      .sort((a, b) => a.toMillis() - b.toMillis());

    if (nextDates.length > 0) {
      handleSingleDayDateChange(nextDates[0]);
    } else {
      const nextMonth = parsedDateTimeTo.plus({months: 1}).startOf('month');
      if (nextMonth > maxDate) {
        return;
      }
      setSearchRange({
        from: nextMonth.startOf('month'),
        to: nextMonth.endOf('month'),
      });
    }
  };

  useEffect(() => {
    if (!parsedDateTimeTo) {
      return;
    }

    const sortedDates = availableDates.sort(
      (a, b) => b.toMillis() - a.toMillis(),
    );

    const currentDateExists = sortedDates.some(date =>
      date.hasSame(parsedDateTimeTo, 'day'),
    );

    if (sortedDates.length > 0 && !currentDateExists) {
      if (sortedDates[0] < parsedDateTimeTo) {
        handleSingleDayDateChange(sortedDates[0]);
      } else {
        handleSingleDayDateChange(sortedDates[sortedDates.length - 1]);
      }
    }
    // should not trigger when parsedDateTimeTo (or parsedDateTimeFrom) changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDates]);

  if (!parsedDateTimeFrom || !parsedDateTimeTo) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      {isSingleDate ? (
        <>
          <IconButton
            onClick={handlePrev}
            aria-label="Previous day"
            sx={{color: 'primary.main'}}
            disabled={parsedDateTimeTo <= minDate || !mapFeature || !layerId}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <DateField
            label="Date"
            size="small"
            value={parsedDateTimeTo}
            onChange={newValue =>
              handleSingleDayDateChange(newValue as DateTime)
            }
            onClick={() => {
              dispatch(showModal(MODALS.ANALYZE_DATE_RANGE));
            }}
            minDate={minDate}
            maxDate={maxDate}
          />
          <IconButton
            onClick={handleNext}
            aria-label="Next day"
            sx={{color: 'primary.main'}}
            disabled={parsedDateTimeTo >= maxDate || !mapFeature || !layerId}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </>
      ) : (
        <>
          <DateField
            label="Start date"
            size="small"
            value={parsedDateTimeFrom}
            onChange={newValue => handleFromDateChange(newValue as DateTime)}
            onClick={() => {
              dispatch(showModal(MODALS.ANALYZE_DATE_RANGE));
            }}
            minDate={minDate}
            maxDate={parsedDateTimeTo}
          />
          –
          <DateField
            label="End date"
            size="small"
            value={parsedDateTimeTo}
            onChange={newValue => handleToDateChange(newValue as DateTime)}
            onClick={() => {
              dispatch(showModal(MODALS.ANALYZE_DATE_RANGE));
            }}
            minDate={parsedDateTimeFrom}
            maxDate={maxDate}
          />
        </>
      )}
    </Box>
  );
};

export default DatePicker;
